import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { withPrefix, graphql, navigate } from 'gatsby';
import type { PageProps } from 'gatsby';

import iconemob from '../../static/ico-mobilite-1.svg';
import iconenum from '../../static/ico-numerique-1.svg';
import iconelog from '../../static/ico-logement-1.svg';
import iconeadmin from '../../static/ico-admin-1.svg';
import iconesenseignement from '../../static/ico-enseignement-1.svg';
import iconeagric from '../../static/ico-agriculture-1.svg';
import iconenrj from '../../static/ico-energie-1.svg';
import iconefin from '../../static/ico-finance-1.svg';
import iconeville from '../../static/ico-ville-1.svg';
import iconeimpact from '../../static/ico-impact-1.svg';
import Layout from '../components/Layout';
import { TitleH2, TitleH3 } from '../components/title';

import InitiativeRowSingle from '../components/initiativeRowSingle';
import DeliverablesRow from '../components/deliverablesRow';

import { LAUNCHING_INITIATIVE } from '../utils';
import { SEO } from '../components/Seo';
import { FormModel, GraphQLNodeType } from '../types';
import { Button, Stack } from '@mui/material';
import { NEED_CONTRIBUTOR_ACTION_MESSAGE } from '../constants';
import { ConditionalTooltipButton } from '../components/common/ConditionalTooltipButton';
import { useUserAccessRights } from '../components/UserProvider';
import { Team } from '../components/Team';

const InitiativeHPLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const InitiativeHPSection = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: auto;

  .LinkFirstRow {
    margin-bottom: 2rem;
  }

  @media only screen and (max-width: 1200px) {
    width: 90%;
    max-width: 90%;
    margin: 0 5%;
  }
`;

/**
 * InitiativeBanner components
 */
const InitiativeBanner = styled.div`
  background-color: #fcfaf4;
`;

const InitiativeHPSectionInside = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: row;
  flex-wrap: wrap;
`;

const InitiativeBannerCol1 = styled.div`
  flex: 1;
  background-color: ${(props) => props.theme.palette.primary.main};
  padding: 3rem;
  display: flex;
  justify-content: flex-end;
`;

const InitiativeBannerCol1Width = styled.div`
  max-width: 600px;

  @media all and (min-width: 1300px) {
    max-width: 553px;
  }
`;

const InitiativeBannerCol2 = styled.div`
  flex: 1;
  align-items: center;
  padding: 3rem;
  background-color: hsl(221deg 61% 88%);
  color: hsl(207deg 17% 21%);
`;

const InitiativeBannerCol2Width = styled.div`
  max-width: 600px;
`;

/**
 * InitiativeRow Components
 */
const InitiativeRow = styled.div`
  background-color: #fcfaf4;
  padding: 1rem 0 3rem 0;
  @media only screen and (max-width: 768px) {
    padding: 1rem 0 3rem 0;
  }
`;

const InitiativeRowH2 = styled.div`
  .titleH2LastRow {
    margin-top: 2rem;
  }

  h2 {
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const InitiativeRowH3Link = styled.div`
  text-decoration: underline;
  text-align: center;
  margin-top: 1rem;
  cursor: pointer;

  :hover {
    color: #41cbab;
  }
`;

/**
 * InitiativeAutonomous
 */
const InitiativeAutonomous = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const InitiativeAutonomousInside = styled.div`
  margin: 0;
  border-radius: 6px;
  width: 100%;
  padding: 1.875rem 0 1.875rem 0;
  position: relative;
  overflow: hidden;
`;

const InitiativeAutonomousTxt = styled.div`
  font-size: 1.25rem;
  letter-spacing: 0;
  line-height: 2rem;
  position: relative;
  z-index: 15;
  text-align: justify;
`;

/**
 * InitiativeThematique
 */
const InitiativeThematique = styled.div`
  background: hsl(221deg 61% 88%);
  padding: 3rem 0;

  h2 {
    margin-bottom: 1.25rem;
  }
`;

const InitiativeThematiqueTxt = styled.div`
  font-size: 1.125rem;
  letter-spacing: 0;
  line-height: 1.625rem;
  margin-bottom: 2.5rem;
`;

const InitiativeThematiqueRow = styled.div`
  display: flex;
  align-items: center;
  margin: 1.25rem 0;
  flex-wrap: wrap;
  flex-direction: row;
`;

const InitiativeThematiqueRowSingle = styled.div`
  flex: 20%;
  text-align: center;
  height: 104px;
  max-width: calc(20% - 1.25rem);
  border-radius: 2px;
  background-color: #ffffff;
  box-shadow: 0 4px 0.3125rem 0 rgba(0, 0, 0, 0.06);
  margin: 0.625rem;
  padding-top: 1.5625rem;

  @media only screen and (max-width: 1200px) {
    flex: 33%;
    max-width: calc(31.67% - 1.25rem);
  }

  @media only screen and (max-width: 768px) {
    flex: 50%;
    max-width: calc(47.5% - 1.25rem);
  }

  img {
    height: 50px;
  }

  cursor: pointer;
`;

const InitiativeThematiqueRowSingleTxt = styled.div`
  color: #203673;

  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.25rem;
`;

/**
 * InitiativeEquipe
 */
const InitiativeEquipe = styled.div`
  padding: 3.125rem 0;
  background: #203673;
  color: #fff;
`;

const InitiativeEquipeInside = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  flex-wrap: wrap;

  @media only screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const InitiativeEquipeCol1 = styled.div`
  flex: 1;
  text-align: justify;
  h2,
  h3 {
    margin-bottom: 1.125rem;
  }

  @media only screen and (max-width: 1200px) {
    padding: 0 1.875rem;
  }

  @media only screen and (max-width: 992px) {
    padding: 0;
  }

  @media only screen and (max-width: 768px) {
    flex: 100%;
  }
`;

const InitiativeEquipeCol2 = styled.div`
  width: 50%;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  align-items: flex-start;
  flex-wrap: wrap;

  padding: 1.25rem 0;

  @media only screen and (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const InitiativeEquipeTxt1 = styled.div`
  margin-top: 0.625rem;

  font-size: 1.25rem;
  letter-spacing: 0;
  line-height: 2rem;
`;

const InitiativeEquipeTxt2 = styled.div`
  font-size: 1.25rem;
  letter-spacing: 0;
  line-height: 2rem;

  ul {
    list-style-type: none;
    padding-left: 1.875rem;

    li {
      position: relative;

      ::before {
        content: '';
        display: block;
        position: absolute;
        height: 5px;
        width: 16px;
        border-radius: 2px;
        background-color: #41cbab;
        top: 0.9375rem;
        left: -1.875rem;
      }
    }
  }
`;

const InitiativeEquipeTxt3 = styled.div`
  font-size: 1.25rem;
  letter-spacing: 0;
  line-height: 2rem;

  a {
    color: #ffffff;
  }
`;

const HomepageTitleH1 = styled.div`
  color: #fff;
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 3rem;
  margin: 0;
`;

const HomepageTitleH2 = styled.div`
  color: #183066;

  font-size: 2rem;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 3rem;
  margin: 0;
`;

const MainButton = styled.button`
  color: ${(props) => (props.color ? props.color : '#ffffff')};
  background: ${(props) => props.background};
  padding: 16px 19px;
  border-radius: 5px;
  border: 2px solid ${(props) => props.background};

  font-size: 12px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 15px;
  text-align: center;
  margin-top: 20px;
  border: none;
  -webkit-transition: all 0.5s;
  -o-transition: all 0.5s;
  transition: all 0.5s;

  cursor: pointer;

  &:hover {
    background: #41cbab;
    color: #ffffff;
  }
`;

type FormType = Pick<FormModel, 'FormID' | 'FormURL'>;

type FormPropsType = PageProps & {
  data: {
    IA_Url: GraphQLNodeType<FormType>;
  };
  location?: any;
};

const IndexPage = ({ data, location }: FormPropsType) => {
  const { canCreateIAInitiative } = useUserAccessRights();

  const IA_URL = data?.IA_Url?.edges[0]?.node?.data?.FormURL;
  const autonomousInitiativesAnchorRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (location.hash === '#initiative-autonome') {
      setTimeout(() => {
        if (autonomousInitiativesAnchorRef.current) {
          autonomousInitiativesAnchorRef.current.scrollIntoView();
        }
      }, 50);
    }
  }, []);

  const thematiques1 = [
    {
      title: `Administration publique`,
      link: `/initiatives/?thematiques=administration-publique`,
      imgSrc: iconeadmin,
    },
    {
      title: `Agriculture et alimentation`,
      link: `/initiatives/?thematiques=agriculture-et-alimentation`,
      imgSrc: iconeagric,
    },
    {
      title: `Emploi et finance`,
      link: `/initiatives/?thematiques=finance,emploi`,
      imgSrc: iconefin,
    },
    {
      title: `Énergie`,
      link: `/initiatives/?thematiques=energie`,
      imgSrc: iconenrj,
    },
    {
      title: `Enseignement supérieur`,
      link: `/initiatives/?thematiques=enseignement-superieur-et-recherche`,
      imgSrc: iconesenseignement,
    },
  ];

  const thematiques2 = [
    {
      title: `Impact et résilience`,
      link: `/initiatives/?thematiques=impacts-et-resilience`,
      imgSrc: iconeimpact,
    },
    {
      title: `Mobilité`,
      link: `/initiatives/?thematiques=mobilite-longue-distance,mobilite-du-quotidien`,
      imgSrc: iconemob,
    },
    {
      title: `Usages Numériques`,
      link: `/initiatives/?thematiques=usages-numeriques`,
      imgSrc: iconenum,
    },
    {
      title: `Vie de l'association`,
      link: `/initiatives/?thematiques=vie-de-lassociation`,
      imgSrc: iconelog,
    },
    {
      title: `Villes et territoires`,
      link: `/initiatives/?thematiques=villes-et-territoires`,
      imgSrc: iconeville,
    },
  ];

  return (
    <Layout>
      <InitiativeHPLayout>
        <InitiativeBanner>
          <InitiativeHPSectionInside>
            <InitiativeBannerCol1>
              <InitiativeBannerCol1Width>
                <HomepageTitleH1>
                  Découvre comment les Shifters contribuent à la transition
                  énergie-climat.
                </HomepageTitleH1>
                <MainButton
                  onClick={() => {
                    navigate(withPrefix(`/initiatives/`));
                  }}
                  color="#ffffff"
                  background="#db3069"
                >
                  Explorer les initiatives
                </MainButton>
              </InitiativeBannerCol1Width>
            </InitiativeBannerCol1>
            <InitiativeBannerCol2>
              <InitiativeBannerCol2Width>
                <HomepageTitleH2>
                  Tu as une idée d’initiative et cherches des Shifters pour lui
                  donner vie ? Lance-toi !
                </HomepageTitleH2>
                <MainButton
                  onClick={() => {
                    navigate(withPrefix(`/#initiative-autonome`));
                  }}
                  color="hsl(0deg 0% 0%)"
                  background="hsl(340deg 75% 92%)"
                >
                  Proposer une initiative
                </MainButton>
              </InitiativeBannerCol2Width>
            </InitiativeBannerCol2>
          </InitiativeHPSectionInside>
        </InitiativeBanner>

        <InitiativeRow>
          <InitiativeHPSection>
            <InitiativeRowH2
              onClick={() => {
                navigate(withPrefix(`/initiatives/?initiative=en-recrutement`));
              }}
            >
              <TitleH2>Les initiatives qui recrutent</TitleH2>
            </InitiativeRowH2>
            <InitiativeRowSingle
              hiringFilter="en-recrutement"
              number={3}
              thematique=""
              text="initiatives qui recrutent"
            ></InitiativeRowSingle>
            <InitiativeRowH3Link
              onClick={() => {
                navigate(withPrefix(`/initiatives/?initiative=en-recrutement`));
              }}
              className="LinkFirstRow"
            >
              Voir toutes les initiatives qui recrutent
            </InitiativeRowH3Link>
            <InitiativeRowH2
              onClick={() => {
                navigate(withPrefix(`/initiatives/?initiative=travaux`));
              }}
            >
              <TitleH2>Les derniers travaux publiés</TitleH2>
            </InitiativeRowH2>
            <DeliverablesRow number={6}></DeliverablesRow>
            <InitiativeRowH2
              onClick={() => {
                navigate(withPrefix('/initiatives/?statuts=en-lancement'));
              }}
            >
              <TitleH2 className="titleH2LastRow">
                Les dernières initiatives lancées
              </TitleH2>
            </InitiativeRowH2>
            <InitiativeRowSingle
              status={LAUNCHING_INITIATIVE}
              number={3}
              thematique=""
              text="initiatives en lancement"
            ></InitiativeRowSingle>
            <InitiativeRowH3Link
              onClick={() => {
                navigate(withPrefix('/initiatives/?statuts=en-lancement'));
              }}
            >
              Voir les initiatives en lancement
            </InitiativeRowH3Link>
          </InitiativeHPSection>
        </InitiativeRow>
        <InitiativeThematique>
          <InitiativeHPSection>
            <TitleH2>Découvrez les thématiques des initiatives</TitleH2>
            <InitiativeThematiqueTxt>
              Un sujet en particulier t’intéresse ? Explore l'ensemble des
              thématiques autour desquelles nous agissons.
            </InitiativeThematiqueTxt>
            <InitiativeThematiqueRow>
              {thematiques1.map((t, idx) => (
                <InitiativeThematiqueRowSingle
                  key={idx}
                  onClick={() => {
                    navigate(withPrefix(t.link));
                  }}
                >
                  <img src={t.imgSrc} alt=""></img>
                  <InitiativeThematiqueRowSingleTxt>
                    {t.title}
                  </InitiativeThematiqueRowSingleTxt>
                </InitiativeThematiqueRowSingle>
              ))}

              {thematiques2.map((t, idx) => (
                <InitiativeThematiqueRowSingle
                  key={idx}
                  onClick={() => {
                    navigate(withPrefix(t.link));
                  }}
                >
                  <img src={t.imgSrc} alt=""></img>
                  <InitiativeThematiqueRowSingleTxt>
                    {t.title}
                  </InitiativeThematiqueRowSingleTxt>
                </InitiativeThematiqueRowSingle>
              ))}
            </InitiativeThematiqueRow>
          </InitiativeHPSection>
        </InitiativeThematique>
        <a
          style={{ scrollMargin: '50px' }}
          id="initiative-autonome"
          ref={autonomousInitiativesAnchorRef}
        />
        <InitiativeAutonomous>
          <InitiativeHPSection>
            <InitiativeAutonomousInside>
              <TitleH2>Les initiatives autonomes</TitleH2>
              <InitiativeAutonomousTxt>
                Tu souhaites t’impliquer activement dans l’association et tu as
                une idée précise de projet ? <br />
                <b>Propose ici ta propre initiative.</b> Après relecture de ta
                proposition par le Conseil d’Administration des Shifters, tu
                pourras bénéficier de l'appui de l'association et de la
                visibilité de ton initiative sur le site afin de réaliser tes
                recrutements de Shifters, partager ton avancement et tes
                conclusions à l’ensemble de la communauté.
              </InitiativeAutonomousTxt>
              <Stack justifyContent="center" alignItems="center" mt="1rem">
                <ConditionalTooltipButton
                  button={
                    <Button
                      size="large"
                      variant="contained"
                      sx={{ textTransform: 'initial' }}
                      href={IA_URL}
                      target="_blank"
                      onClick={() => {
                        window.dataLayer.push({ event: 'IA' });
                      }}
                    >
                      Proposer une initiative
                    </Button>
                  }
                  isDisabled={!canCreateIAInitiative}
                  tooltipProps={{
                    title: NEED_CONTRIBUTOR_ACTION_MESSAGE,
                    placement: 'top',
                  }}
                />
              </Stack>
            </InitiativeAutonomousInside>
          </InitiativeHPSection>
        </InitiativeAutonomous>
        <InitiativeEquipe>
          <InitiativeHPSection>
            <InitiativeEquipeInside>
              <InitiativeEquipeCol1>
                <TitleH2 color="#ffffff">Le Domaine Projets</TitleH2>
                <TitleH3 color="#ffffff">L'équipe</TitleH3>
                <InitiativeEquipeTxt1>
                  Le Domaine Projets a pour vocation d’aider les Shifters sur
                  les initiatives (projets ou activités) pour :
                </InitiativeEquipeTxt1>
                <InitiativeEquipeTxt2>
                  <ul>
                    <li>leur initiation</li>
                    <li>leur suivi</li>
                    <li>leur accompagnement</li>
                    <li>leur vérification</li>
                    <li>leur gestion des “initiatives autonomes”</li>
                  </ul>
                </InitiativeEquipeTxt2>
                <InitiativeEquipeTxt3>
                  L’objectif étant de soutenir chaque initiative, et d’avoir un
                  fonctionnement commun et standardisé pour améliorer l’impact
                  de chacune des actions menées.
                </InitiativeEquipeTxt3>
              </InitiativeEquipeCol1>
              <InitiativeEquipeCol2>
                <Team />
              </InitiativeEquipeCol2>
            </InitiativeEquipeInside>
          </InitiativeHPSection>
        </InitiativeEquipe>
      </InitiativeHPLayout>
    </Layout>
  );
};

export const query = graphql`
  query IA_Url {
    IA_Url: allAirtable(
      filter: { table: { eq: "Forms" }, data: { FormID: { eq: "IA" } } }
    ) {
      edges {
        node {
          data {
            FormURL
          }
        }
      }
    }
  }
`;

export default IndexPage;

export const Head = () => <SEO />;
